import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetManagement, Header } from '@aptsi-types';
import { MatDialog } from '@angular/material/dialog';
import { DataAssetSourceService } from './services/data-asset-source.service';
import { DeleteDialogComponent } from 'src/app/shared/dialogs/delete-dialog/delete-dialog.component';
import { DataAssetsCaptureForFileAssetSourceComponent } from './data-assets-capture-for-file-asset-source/data-assets-capture-for-file-asset-source.component';
import { DataAssetsCaptureForAssetSourceComponent } from './data-assets-capture-for-asset-source/data-assets-capture-for-asset-source.component';
import { DataAssetsViewComponent } from './data-assets-view/data-assets-view.component';
import {
  WorkflowNavigationService
} from '../shared/workflow-navigation.service';

interface AssetSourcePaginationMeta {
  totalAssetSources?: number;
  totalPages?: number;
  currentPage?: number;
}

type AssetManagementWithMeta = AssetManagement & AssetSourcePaginationMeta;
type AssetRecord = AssetManagement & Record<string, unknown>;

@Component({
  selector: 'app-data-asset-capture',
  templateUrl: './data-asset-capture.component.html',
  styleUrls: ['./data-asset-capture.component.scss'],
})
export class DataAssetCaptureComponent implements OnInit {
  readonly itemsPerPage = 10;

  currentPage = 1;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  isAdd = false;
  issearch = false;
  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;
  hasDataAssetSources = false;

  searchText = '';
  filterByHeader = 'all';

  filteredData: AssetManagement[] = [];
  searchedData: AssetManagement[] = [];

  /** Prevent a slower, superseded request from overwriting the active page/search result. */
  private requestSequence = 0;

  // The selected source's data assets are shown in the detail panel below the list.
  selectedAssetSource: AssetManagement | null = null;
  dataAssets: AssetManagement[] = [];
  isLoadingDataAssets = false;
  isImporting = false;
  importingSourceId = '';
  dataAssetsError = '';
  actionMessage = '';

  get displayedData(): AssetManagement[] {
    return this.issearch ? this.searchedData : this.filteredData;
  }

  readonly headers: Header[] = [
    {
      name: 'Asset Source Id',
      property: 'assetSourceId',
      showInDropdown: true,
      description: 'Unique identifier for the data asset source',
    },
    {
      name: 'Asset Source Name',
      property: 'assetSourceName',
      showInDropdown: true,
      description: 'Name of the data asset source',
    },
    {
      name: 'Asset Source',
      property: 'assetSource',
      showInDropdown: true,
      description: 'Platform or origin of the data assets',
    },
    {
      name: 'Asset Source Type',
      property: 'assetSourceType',
      showInDropdown: true,
      description: 'Integration type used by the data asset source',
    },
    {
      name: 'Last Updated',
      property: 'updatedAt',
      showInDropdown: true,
      description: 'Date and time when the source was last updated',
    },
  ];

  // Microsoft Purview is the only supported connected data-source integration at present.
  readonly filterOptions: Record<string, { value: string; label: string }[]> = {
    assetSourceType: [      
      { label: 'DATA_GOVERNANCE', value: 'DATA_GOVERNANCE' },

      { label: 'MANUAL_ENTRY', value: 'MANUAL_ENTRY' },

      { label: 'FILE', value: 'FILE' },
    ],
    assetType: [{ label: 'Data', value: 'data' }],
  };

  readonly buttonConfig = {
    update: true,
    delete: true,
    import: true,
    configure: false,
    view: true,
  };

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dataAssetSourceService: DataAssetSourceService,
    private readonly dialog: MatDialog,
    private workflowNavigation: WorkflowNavigationService
  ) {}

  ngOnInit(): void {
    this.isAdd = this.route.snapshot.queryParamMap.get('isAdd') === 'true';
    void this.fetchAssetSources();
  }

  handleAction(event: {
    action: string;
    rowIndex: number;
    rowData: AssetManagement;
  }): void {
    switch (event.action) {
      case 'update':
        this.updateAssetSource(event.rowIndex);
        break;
      case 'delete':
        void this.deleteAssetSource(event.rowIndex);
        break;
      case 'import':
        void this.importDataAssets(event.rowData);
        break;
      case 'view':
        void this.viewDataAssets(event.rowData);
        break;
      default:
        break;
    }
  }

  /** Fetch one server-paginated page of Data Asset Sources. */
  async fetchAssetSources(): Promise<void> {
    const requestId = ++this.requestSequence;
    const requestedPage = Math.max(1, Math.floor(Number(this.currentPage) || 1));

    this.currentPage = requestedPage;
    this.isLoading = true;
    this.isSearching = false;
    this.loadError = false;

    try {
      const apiResponse = await this.dataAssetSourceService
        .FetchDataAssetSources(this.toApiPageIndex(requestedPage))
        .toPromise();

      // Ignore stale results when a newer page or search request has started.
      if (requestId !== this.requestSequence || this.issearch) {
        return;
      }

      const response: AssetManagementWithMeta[] = Array.isArray(apiResponse)
        ? (apiResponse as AssetManagementWithMeta[])
        : [];
      const metadata = response[0];

      // The endpoint already returns a single server-paginated page.
      this.filteredData = response.filter((source) => this.isDataAssetSource(source));

      // Use backend pagination metadata directly; do not estimate page count on the client.
      this.totalItemsNormal =
        this.readNonNegativeInteger(metadata?.totalAssetSources) ?? this.filteredData.length;
      this.totalPagesNormal = this.readNonNegativeInteger(metadata?.totalPages) ?? 0;
      this.totalPages = this.totalPagesNormal;

      this.hasDataAssetSources = this.totalItemsNormal > 0;

      if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      } else if (requestedPage > this.totalPagesNormal) {
        // If the result set shrinks, reload the last valid page.
        this.currentPage = this.totalPagesNormal;
        await this.fetchAssetSources();
        return;
      } else {
        // Keep the one-based page selected in the UI. Do not overwrite it with API currentPage.
        this.currentPage = requestedPage;
      }
    } catch (error: unknown) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error fetching data asset sources:', error);
      this.loadError = true;
      this.filteredData = [];
      this.hasDataAssetSources = false;
      this.totalItemsNormal = 0;
      this.totalPagesNormal = 0;
      this.totalPages = 0;
      this.currentPage = 1;
    } finally {
      if (requestId === this.requestSequence) {
        this.isLoading = false;
      }
    }
  }

  /** Search results are server-paginated; don't apply the search filter a second time locally. */
  async fetchAssetSourcesSearch(): Promise<void> {
    const requestId = ++this.requestSequence;
    const requestedPage = Math.max(1, Math.floor(Number(this.currentPage) || 1));

    this.currentPage = requestedPage;
    this.isLoading = false;
    this.isSearching = true;
    this.searchError = false;

    try {
      const apiResponse = await this.dataAssetSourceService
        .FetchDataAssetSourcesSearch(
          this.toApiPageIndex(requestedPage),
          this.searchText.trim(),
          this.filterByHeader,
        )
        .toPromise();

      if (requestId !== this.requestSequence || !this.issearch) {
        return;
      }

      const response: AssetManagementWithMeta[] = Array.isArray(apiResponse)
        ? (apiResponse as AssetManagementWithMeta[])
        : [];
      const metadata = response[0];
      this.searchedData = response.filter((source) => this.isDataAssetSource(source));

      this.totalItemsSearch =
        this.readNonNegativeInteger(metadata?.totalAssetSources) ?? this.searchedData.length;
      this.totalPagesSearch = this.readNonNegativeInteger(metadata?.totalPages) ?? 0;
      this.totalPages = this.totalPagesSearch;

      if (this.totalPagesSearch === 0) {
        this.currentPage = 1;
      } else if (requestedPage > this.totalPagesSearch) {
        this.currentPage = this.totalPagesSearch;
        await this.fetchAssetSourcesSearch();
        return;
      } else {
        this.currentPage = requestedPage;
      }
    } catch (error: unknown) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error searching data asset sources:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      this.totalPages = 0;
      this.currentPage = 1;
    } finally {
      if (requestId === this.requestSequence) {
        this.isSearching = false;
      }
    }
  }

  async searchAssetSources(): Promise<void> {
    const query = this.searchText.trim();

    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.issearch = true;
    this.currentPage = 1;
    this.searchError = false;
    this.searchedData = [];
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;

    await this.fetchAssetSourcesSearch();
  }

  async clearSearch(): Promise<void> {
    this.searchText = '';
    this.issearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.searchedData = [];
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;

    await this.fetchAssetSources();
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    // A filter change starts a new server-side search while preserving the query.
    if (this.searchText.trim()) {
      this.searchText = this.searchText.trim();
      this.issearch = true;
      this.currentPage = 1;
      this.searchedData = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      void this.fetchAssetSourcesSearch();
      return;
    }

    void this.clearSearch();
  }

  changePage(page: number): void {
    const activeTotalPages = this.issearch ? this.totalPagesSearch : this.totalPagesNormal;

    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > activeTotalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.currentPage = page;
    if (this.issearch) {
      void this.fetchAssetSourcesSearch();
    } else {
      void this.fetchAssetSources();
    }
  }

  async deleteAssetSource(index: number): Promise<void> {
    const source = this.displayedData[index];
    const assetSourceId = String(source?.assetSourceId ?? '').trim();
    if (!assetSourceId) return;

    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Are you sure?',
          message: 'Do you really want to delete this Data Asset Source?',
          isConfirm: false,
        },
      });
      const confirmed = await dialogRef.afterClosed().toPromise();
      if (!confirmed) return;

      await this.dataAssetSourceService.deleteAssetSource(assetSourceId).toPromise();
      this.issearch = false;
      this.searchText = '';
      this.currentPage = 1;
      this.searchedData = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      await this.fetchAssetSources();

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Success',
          message: 'The Data Asset Source has been deleted successfully.',
          isConfirm: true,
        },
      });
    } catch (error: unknown) {
      console.error('Error deleting data asset source:', error);
      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Unable to delete source',
          message: this.getErrorMessage(error) || 'The data asset source could not be deleted.',
          isConfirm: true,
        },
      });
    }
  }

 updateAssetSource(index: number): void {
  const source = this.displayedData[index];
  const assetSourceId = String(source?.assetSourceId ?? '').trim();

  if (!assetSourceId) {
    return;
  }

  const isFileSource =
    String(source?.assetSourceType ?? '').trim().toUpperCase() === 'FILE';

  if (isFileSource) {
    void this.router.navigate(['file', 'update', assetSourceId], {
      relativeTo: this.route,
    });
  } else {
    void this.router.navigate(['update', assetSourceId], {
      relativeTo: this.route,
    });
  }
}

  async importDataAssets(source: AssetManagement): Promise<void> {
    const assetSourceId = String(source.assetSourceId ?? '').trim();
    const sourceType = String(source.assetSourceType ?? '').trim().toUpperCase();
    if (!assetSourceId) return;

    if (sourceType === 'FILE') {
      const dialogRef = this.dialog.open(DataAssetsCaptureForFileAssetSourceComponent, {
        width: '1100px',
        maxWidth: '96vw',
        maxHeight: '94vh',
        autoFocus: false,
        disableClose: true,
        data: {
          assetSourceId,
          assetSourceName: source.assetSourceName ?? '',
          assetSourceType: source.assetSourceType ?? '',
          assetSource: source.assetSource ?? '',
        },
      });
      dialogRef.afterClosed().subscribe((result?: { imported?: boolean }) => {
        if (result?.imported) {
          if (this.issearch) void this.fetchAssetSourcesSearch();
          else void this.fetchAssetSources();
        }
      });
      return;
    }

    // if (sourceType !== 'PURVIEW') {
    //   this.dialog.open(DeleteDialogComponent, {
    //     data: {
    //       title: 'Import not available',
    //       message: 'Import from a connected source is currently supported for Microsoft Purview only.',
    //       isConfirm: true,
    //     },
    //   });
    //   return;
    // }

    // this.isImporting = true;
    // this.importingSourceId = assetSourceId;
    // this.actionMessage = '';
    // this.dataAssetsError = '';

    // try {
    //   const response = await 
    //     this.dataAssetSourceService.ImportDataAssetsByAssetSource(assetSourceId).toPromise();
      
    //   const successMessage = response?.message || 'Data assets were imported successfully.';
    //   await this.viewDataAssets(source);
    //   this.actionMessage = successMessage;
    //   if (this.issearch) await this.fetchAssetSourcesSearch();
    //   else await this.fetchAssetSources();
    // } catch (error: unknown) {
    //   console.error('Error importing data assets:', error);
    //   this.actionMessage = '';
    //   this.dataAssetsError = this.getErrorMessage(error) || 'Unable to import data assets from Microsoft Purview.';
    // } finally {
    //   this.isImporting = false;
    //   this.importingSourceId = '';
    // }

      // Keep the existing non-file import workflow.
      const dialogRef = this.dialog.open(
        DataAssetsCaptureForAssetSourceComponent,
        {
          width: '520px',
          maxWidth: '94vw',
          maxHeight: '90vh',
          autoFocus: false,
          disableClose: true,
          data: {
            assetSourceId: String(source.assetSourceId),
            assetSourceName: source.assetSourceName ?? ''
          }
        }
      );
    
      dialogRef.afterClosed().subscribe(
        (result?: { shouldViewSystemAssets?: boolean }) => {
          if (result?.shouldViewSystemAssets) {
            this.viewDataAssets(source);
          }
        }
      );
  }

  async viewDataAssets(source: AssetManagement): Promise<void> {
    // const assetSourceId = String(source.assetSourceId ?? '').trim();
    // if (!assetSourceId) return;

    // this.selectedAssetSource = source;
    // this.dataAssets = [];
    // this.isLoadingDataAssets = true;
    // this.dataAssetsError = '';
    // this.actionMessage = '';

    // try {
    //   const response = await 
    //     this.dataAssetSourceService.FetchDataAssetsByAssetSourceID(assetSourceId, 0).toPromise();
      
    //   const assets = Array.isArray(response) ? response : [];
    //   this.dataAssets = assets.filter((asset) => this.isDataAssetRecord(asset));
    //   if (!this.dataAssets.length && assets.length) {
    //     this.dataAssets = assets.filter((asset) => this.hasAnyAssetIdentifier(asset));
    //   }
    // } catch (error: unknown) {
    //   console.error('Error fetching data assets for source:', error);
    //   this.dataAssetsError = this.getErrorMessage(error) || 'Unable to fetch the data assets for this source.';
    // } finally {
    //   this.isLoadingDataAssets = false;
    // }
      const assetSourceId = String(source.assetSourceId ?? '').trim();
    
    
    
        if (!assetSourceId) {
    
          console.error(
    
            'Cannot view system assets: assetSourceId is missing.',
    
            source
    
          );
    
          return;
    
        }
    
    
    
        this.dialog.open(DataAssetsViewComponent, {
    
          width: '95vw',
    
          maxWidth: '1500px',
    
          maxHeight: '95vh',
    
          autoFocus: false,
    
          data: {
    
            assetSourceId,
    
            assetSourceName: source.assetSourceName ?? ''
    
          }
    
        });
    
  }

  closeDataAssets(): void {
    this.selectedAssetSource = null;
    this.dataAssets = [];
    this.dataAssetsError = '';
    this.actionMessage = '';
  }

  addAssetSource(): void {
    this.isAdd = true;
  }

createAssetSource(): void {
  void this.router.navigate(['create'], {
    relativeTo: this.route,
    queryParams: {
      isAdd: true
    }
  });
}

addAssetThroughFile(): void {
  void this.router.navigate(['file', 'create'], {
    relativeTo: this.route,
    queryParams: { isAdd: true },
  });
}

addManualAsset(): void {
  void this.router.navigate(['create-asset'], {
    relativeTo: this.route,
    queryParams: { isAdd: true },
  });
}

backToSourceList(): void {
  this.isAdd = false;
}

  // back(): void {
  //   if (this.issearch) {
  //     void this.clearSearch();
  //     return;
  //   }
  //   void this.router.navigate(['asset-management/auth-tools']);
  // }

  assetValue(asset: AssetManagement, ...keys: string[]): string {
    const record = asset as unknown as AssetRecord;
    for (const key of keys) {
      const value = record[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value);
      }
    }
    return '—';
  }

  private isDataAssetSource(source: AssetManagement): boolean {
    return String((source as unknown as Record<string, unknown>)['assetType'] ?? '')
      .trim()
      .toLowerCase() === 'data';
  }

  private isDataAssetRecord(asset: AssetManagement): boolean {
    const record = asset as unknown as AssetRecord;
    return [
      'DATAAssetName', 'dataAssetName', 'dataAssetId', 'DATAAssetId',
      'assetName', 'assetId', 'asset_name', 'asset_id',
    ].some((key) => record[key] !== undefined && record[key] !== null && String(record[key]).trim() !== '');
  }

  private hasAnyAssetIdentifier(asset: AssetManagement): boolean {
    return this.isDataAssetRecord(asset) || Boolean(this.assetValue(asset, 'DATAAssetDescription', 'dataAssetDescription') !== '—');
  }

  /** Convert the one-based UI page to the zero-based page index expected by the API. */
  private toApiPageIndex(uiPage: number): number {
    return Math.max(0, Math.floor(uiPage) - 1);
  }

  /** Read a valid non-negative integer from backend pagination metadata. */
  private readNonNegativeInteger(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : undefined;
  }

  private getErrorMessage(error: unknown): string {
    if (typeof error === 'string') return error;
    if (!error || typeof error !== 'object') return '';
    const record = error as Record<string, unknown>;
    const nestedError = record['error'];
    if (typeof nestedError === 'string') return nestedError;
    if (nestedError && typeof nestedError === 'object') {
      const nestedMessage = (nestedError as Record<string, unknown>)['message'];
      if (typeof nestedMessage === 'string') return nestedMessage;
    }
    return typeof record['message'] === 'string' ? String(record['message']) : '';
  }
}
